/**
 * API 相关类型定义
 */

// 基础响应结构
export interface BaseResponse<T = any> {
  code: number
  message: string
  data: T
  timestamp: number
  success: boolean
}

// 分页响应结构
export interface PaginatedResponse<T = any> extends BaseResponse<T[]> {
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

// 请求配置类型
export interface RequestConfig {
  url: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  data?: any
  params?: Record<string, any>
  headers?: Record<string, string>
  timeout?: number
  showLoading?: boolean
  showError?: boolean
  useQueue?: boolean // 是否使用请求队列（默认使用，可提高并发控制和防止500错误）
  allowDedupe?: boolean // 是否允许请求去重（默认允许，相同请求共享结果）
}

// 请求拦截器类型
export interface RequestInterceptor {
  onFulfilled?: (config: RequestConfig) => RequestConfig | Promise<RequestConfig>
  onRejected?: (error: any) => any
}

// 响应拦截器类型
export interface ResponseInterceptor {
  onFulfilled?: (response: any) => any
  onRejected?: (error: any) => any
}

// HTTP 状态码枚举
export enum HttpStatus {
  OK = 200,
  CREATED = 201,
  NO_CONTENT = 204,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  METHOD_NOT_ALLOWED = 405,
  CONFLICT = 409,
  UNPROCESSABLE_ENTITY = 422,
  TOO_MANY_REQUESTS = 429,
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504
}

// 错误类型
export interface ApiError {
  code: number
  message: string
  details?: any
  timestamp: number
}

// 业务错误类型
export interface BusinessError extends Error {
  code: number
  response?: any
  url?: string
  method?: string
  data?: any
}

// 请求方法类型
export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

// 请求参数类型
export interface RequestParams {
  [key: string]: string | number | boolean | null | undefined
}

// 请求体类型
export type RequestBody = Record<string, any> | FormData | string | null

// 响应数据类型
export type ResponseData<T = any> = T

// 上传文件类型
export interface UploadFile {
  file: File
  name?: string
  url?: string
  progress?: number
}

// 下载文件配置
export interface DownloadConfig {
  url: string
  filename?: string
  headers?: Record<string, string>
}

// 缓存配置
export interface CacheConfig {
  enabled: boolean
  ttl: number // 缓存时间（毫秒）
  key?: string
}

// 重试配置
export interface RetryConfig {
  enabled: boolean
  maxRetries: number
  retryDelay: number
  retryCondition?: (error: any) => boolean
}

// 请求选项
export interface RequestOptions {
  timeout?: number
  showLoading?: boolean
  showError?: boolean
  cache?: CacheConfig
  retry?: RetryConfig
  headers?: Record<string, string>
  signal?: AbortSignal
}

// 用户相关 API 类型
export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: {
    id: string
    nickname: string
    avatar: string
    email: string
    level: number
    experience: number
  }
  expiresIn: number
}

export interface RegisterRequest {
  email: string
  password: string
  nickname: string
  captcha: string
}

/* 用户信息 */
export interface UserProfile {
  strengthBalance?:any
  raceText?: string
  avatar: string
  balance: number
  energy: number
  birthYear: number
  createdAt: number
  email: string
  nickname: string
  sex: number
  noticeSwitch:number
  subscribeExpired: number
  userExtend: {
    height: number
    weight: number
    race: number
    hobby: string
    favorite_food: string
    character: string
    desired_partner: string
  }
  role: number
  userId: string
}

/* 角色列表 */
export interface RoleList {
  data: any
  id: number
  name: string
  description: string
  personality: string
  firstMes: string
  avatar: string
  scenario: string
  backgroundThumb: string
  background: string
  creatorNotes: string
  systemPrompt: string
  tags: string[]
  characterBook: null
  state: number
  isRecommend: number
  chatCount: number
  chatCountVirtual: number
  favoriteCount: number
  isFavorite: boolean
}
